import React, { useState, useEffect } from 'react'
import {
  User,
  Bell,
  Save,
  Camera,
  ArrowLeft,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import {
  fetchPreferenceByUserId,
  uploadPublicFile,
  upsertPreference,
} from '../lib/instantData'
import GlassCard from '../components/ui/GlassCard'
import GradientButton from '../components/ui/GradientButton'
import PanelShell from '../components/ui/PanelShell'
import ImageCropper from '../components/ui/ImageCropper'
import DatePicker from '../components/ui/DatePicker'

interface UserProfile {
  fullName: string
  email: string
  phone: string
  location: string
  dateOfBirth: string
  fitnessGoals: string[]
  preferredWorkoutTime: string
  avatar_url?: string
}

const DashboardSettings: React.FC = () => {
  const { user, updateUser } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('profile')
  const [loading, setLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // Profile picture state
  const [profilePicture, setProfilePicture] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [showCropper, setShowCropper] = useState(false)

  const [profile, setProfile] = useState<UserProfile>({
    fullName: user?.user_metadata?.full_name || 'Demo User',
    email: user?.email || 'demo@example.com',
    phone: '+1 (555) 123-4567',
    location: 'New York, NY',
    dateOfBirth: '1995-06-15',
    fitnessGoals: ['Weight Loss', 'Muscle Building'],
    preferredWorkoutTime: 'morning',
    avatar_url: user?.user_metadata?.avatar_url || ''
  })

  const [notifications, setNotifications] = useState({
    emailWorkoutReminders: true,
    emailProgressUpdates: true,
    emailNewsletter: false,
    pushWorkoutReminders: true,
    pushProgressUpdates: true,
    smsReminders: false
  })

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ]

  const fitnessGoalOptions = [
    'Weight Loss', 'Muscle Building', 'Endurance', 'Flexibility', 
    'General Fitness', 'Strength Training', 'Rehabilitation'
  ]

  // Phone number formatting function
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const numbers = value.replace(/\D/g, '')
    
    // Always start with +1 for US numbers
    if (numbers.length === 0) return '+1 '
    
    // If it starts with 1, keep it; otherwise add 1
    const withCountryCode = numbers.startsWith('1') ? numbers : '1' + numbers
    
    // Format: +1 (XXX) XXX-XXXX
    if (withCountryCode.length <= 1) return '+1 '
    if (withCountryCode.length <= 4) return `+1 (${withCountryCode.slice(1)}`
    if (withCountryCode.length <= 7) return `+1 (${withCountryCode.slice(1, 4)}) ${withCountryCode.slice(4)}`
    return `+1 (${withCountryCode.slice(1, 4)}) ${withCountryCode.slice(4, 7)}-${withCountryCode.slice(7, 11)}`
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setProfile(prev => ({ ...prev, phone: formatted }))
  }

  // Load profile data from InstantDB
  const loadProfileData = async () => {
    if (!user?.id) return

    try {
      const preferencesData = await fetchPreferenceByUserId(user.id)
      if (preferencesData) {
        setProfile((prev) => ({
          ...prev,
          fullName: preferencesData.full_name || prev.fullName,
          email: preferencesData.email || prev.email,
          phone: preferencesData.phone
            ? formatPhoneNumber(preferencesData.phone)
            : prev.phone,
          location: preferencesData.location || prev.location,
          dateOfBirth: preferencesData.date_of_birth || prev.dateOfBirth,
          fitnessGoals: preferencesData.fitness_goals || prev.fitnessGoals,
          preferredWorkoutTime:
            preferencesData.preferred_workout_time || prev.preferredWorkoutTime,
          avatar_url: preferencesData.avatar_url || prev.avatar_url,
        }))
        if (preferencesData.notification_preferences) {
          setNotifications((prev) => ({
            ...prev,
            ...preferencesData.notification_preferences,
          }))
        }
      }          
    } catch (error) {
      console.error('Error loading profile data:', error)
    }
  }

  // Load profile data when component mounts or user changes
  useEffect(() => {
    if (user) {
      loadProfileData()
    }
  }, [user])

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const handleProfileSave = async () => {
    setLoading(true)
    setErrorMessage('')
    setSuccessMessage('')

    try {
      // Update user metadata in Supabase auth
      const { error: authError } = await updateUser({
        data: {
          full_name: profile.fullName,
        }
      })

      if (authError) {
        setErrorMessage('Failed to update auth profile: ' + authError.message)
        setLoading(false)
        return
      }

      await upsertPreference(user!.id, {
        email: profile.email,
        fullName: profile.fullName,
        phone: profile.phone || undefined,
        location: profile.location || undefined,
        dateOfBirth: profile.dateOfBirth || undefined,
        fitnessGoals: profile.fitnessGoals,
        workoutTime: profile.preferredWorkoutTime || undefined,
        avatarUrl: profile.avatar_url || undefined,
      })

      setSuccessMessage('Profile updated successfully!')
    } catch (error) {
      setErrorMessage('An error occurred while saving your profile')
    }

    setLoading(false)
    setTimeout(() => {
      setSuccessMessage('')
      setErrorMessage('')
    }, 5000)
  }
  const handleNotificationSave = async () => {
    setLoading(true)
    setErrorMessage('')
    try {
      await upsertPreference(user!.id, {
        email: profile.email,
        fullName: profile.fullName,
        notificationPreferences: notifications,
      })
      setSuccessMessage('Notification preferences updated!')
    } catch (error) {
      setErrorMessage('An error occurred while saving your notification preferences')
    }
    setLoading(false)
    setTimeout(() => {
      setSuccessMessage('')
      setErrorMessage('')
    }, 3000)
  }
  const handleProfilePictureUpload = async (file: File) => {
    setUploading(true)
    setErrorMessage('')

    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${user?.id}-${Math.random()}.${fileExt}`
      const filePath = `avatars/${fileName}`

      const uploaded = await uploadPublicFile(filePath, file)
      if (!uploaded.url) {
        setErrorMessage('Failed to upload image')
        setUploading(false)
        return
      }

      await upsertPreference(user!.id, {
        email: profile.email || user!.email || '',
        fullName: profile.fullName || user!.user_metadata?.full_name || 'Client',
        avatarUrl: uploaded.url,
      })

      setProfile((prev) => ({ ...prev, avatar_url: uploaded.url }))
      setSuccessMessage('Profile picture updated successfully!')
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }

    } catch (error) {
      setErrorMessage('An error occurred while uploading your profile picture')
      // Clean up preview URL on error
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }
    }

    setUploading(false)
    setTimeout(() => {
      setSuccessMessage('')
      setErrorMessage('')
    }, 5000)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please select an image file')
        return
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('File size must be less than 5MB')
        return
      }

      setProfilePicture(file)
      setShowCropper(true)
    }
  }

  const handleCropComplete = async (croppedBlob: Blob) => {
    // Convert blob to file
    const croppedFile = new File([croppedBlob], 'profile-picture.jpg', {
      type: 'image/jpeg'
    })
    
    // Create preview URL
    const objectUrl = URL.createObjectURL(croppedBlob)
    setPreviewUrl(objectUrl)
    
    // Upload the cropped image
    await handleProfilePictureUpload(croppedFile)
  }

  const handleGoalToggle = (goal: string) => {
    setProfile(prev => ({
      ...prev,
      fitnessGoals: prev.fitnessGoals.includes(goal)
        ? prev.fitnessGoals.filter(g => g !== goal)
        : [...prev.fitnessGoals, goal]
    }))
  }

  const renderProfileTab = () => (
    <div className="space-y-6">
      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {errorMessage}
        </div>
      )}
      
      {/* Profile Picture */}
      <div className="flex items-center gap-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-300 bg-gray-100 flex items-center justify-center">
            {previewUrl || profile.avatar_url || user?.user_metadata?.avatar_url ? (
              <img 
                src={previewUrl || profile.avatar_url || user?.user_metadata?.avatar_url} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="w-12 h-12 text-gray-400" />
            )}
          </div>
          <label 
            htmlFor="avatar-upload"
            className="absolute -bottom-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-black hover:bg-gray-100 transition-colors border border-gray-300 cursor-pointer"
          >
            <Camera className="w-4 h-4" />
          </label>
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            disabled={uploading}
          />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-ink mb-1">Profile Picture</h3>
          <p className="text-gray-600 text-sm mb-3">Upload a new avatar to personalize your account</p>
          <label 
            htmlFor="avatar-upload"
            className="text-brand hover:text-brand-dark text-sm font-medium cursor-pointer"
          >
            {uploading ? 'Uploading...' : 'Choose File'}
          </label>
        </div>
      </div>

      {/* Personal Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="form-label">
            Full Name
          </label>
          <input
            type="text"
            value={profile.fullName}
            onChange={(e) => setProfile(prev => ({ ...prev, fullName: e.target.value }))}
            className="form-input"
          />
        </div>

        <div>
          <label className="form-label">
            Email Address
          </label>
          <input
            type="email"
            value={profile.email}
            onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
            className="form-input"
          />
        </div>

        <div>
          <label className="form-label">
            Phone Number
          </label>
          <input
            type="tel"
            value={profile.phone}
            onChange={handlePhoneChange}
            placeholder="+1 (555) 123-4567"
            maxLength={18}
            className="form-input"
          />
        </div>

        <div>
          <label className="form-label">
            Location
          </label>
          <input
            type="text"
            value={profile.location}
            onChange={(e) => setProfile(prev => ({ ...prev, location: e.target.value }))}
            className="form-input"
          />
        </div>

                            <div>
                      <label className="form-label">
                        Date of Birth
                      </label>
                      <DatePicker
                        value={profile.dateOfBirth}
                        onChange={(date) => setProfile(prev => ({ ...prev, dateOfBirth: date }))}
                        placeholder="mm/dd/yyyy"
                        className="form-input"
                      />
                    </div>

        <div>
          <label className="form-label">
            Preferred Workout Time
          </label>
          <select
            value={profile.preferredWorkoutTime}
            onChange={(e) => setProfile(prev => ({ ...prev, preferredWorkoutTime: e.target.value }))}
            className="form-input"
          >
            <option value="morning">Morning (6AM - 10AM)</option>
            <option value="afternoon">Afternoon (12PM - 4PM)</option>
            <option value="evening">Evening (6PM - 9PM)</option>
            <option value="flexible">Flexible</option>
          </select>
        </div>
      </div>

      {/* Fitness Goals */}
      <div>
        <label className="form-label">
          Fitness Goals
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {fitnessGoalOptions.map((goal) => (
            <button
              key={goal}
              onClick={() => handleGoalToggle(goal)}
              className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 border border-[#2c2c2c] ${
                profile.fitnessGoals.includes(goal)
                  ? 'bg-ink text-white border-ink'
                  : 'bg-white text-gray-600 hover:bg-brand-soft border-gray-200'
              }`}
            >
              {goal}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <GradientButton
          onClick={handleProfileSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </GradientButton>
      </div>
    </div>
  )

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-ink mb-4">Email Notifications</h3>
        <div className="space-y-4">
          {[
            { key: 'emailWorkoutReminders', label: 'Workout Reminders', description: 'Get reminded about your upcoming training sessions' },
            { key: 'emailProgressUpdates', label: 'Progress Updates', description: 'Weekly summaries of your fitness progress' },
            { key: 'emailNewsletter', label: 'Newsletter', description: 'Monthly fitness tips and updates from Cassie' }
          ].map(({ key, label, description }) => (
            <div key={key} className="flex items-center justify-between p-4 bg-cream rounded-2xl border border-gray-100">
              <div>
                <h4 className="font-medium text-ink">{label}</h4>
                <p className="text-sm text-gray-600">{description}</p>
              </div>
              <button
                onClick={() => setNotifications(prev => ({ ...prev, [key]: !prev[key as keyof typeof prev] }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  notifications[key as keyof typeof notifications] ? 'bg-brand' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute w-5 h-5 bg-white rounded-full transition-transform ${
                  notifications[key as keyof typeof notifications] ? 'translate-x-6' : 'translate-x-0.5'
                } top-0.5`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-ink mb-4">Push Notifications</h3>
        <div className="space-y-4">
          {[
            { key: 'pushWorkoutReminders', label: 'Workout Reminders', description: 'Push notifications for upcoming sessions' },
            { key: 'pushProgressUpdates', label: 'Progress Updates', description: 'Notifications about your achievements' }
          ].map(({ key, label, description }) => (
            <div key={key} className="flex items-center justify-between p-4 bg-cream rounded-2xl border border-gray-100">
              <div>
                <h4 className="font-medium text-ink">{label}</h4>
                <p className="text-sm text-gray-600">{description}</p>
              </div>
              <button
                onClick={() => setNotifications(prev => ({ ...prev, [key]: !prev[key as keyof typeof prev] }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  notifications[key as keyof typeof notifications] ? 'bg-brand' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute w-5 h-5 bg-white rounded-full transition-transform ${
                  notifications[key as keyof typeof notifications] ? 'translate-x-6' : 'translate-x-0.5'
                } top-0.5`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <GradientButton
          onClick={handleNotificationSave}
          disabled={loading}
          className="flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {loading ? 'Saving...' : 'Save Preferences'}
        </GradientButton>
      </div>
    </div>
  )

  return (
    <>
      <PanelShell
        eyebrow="Account"
        title="Settings"
        subtitle="Manage your profile and notification preferences."
        actions={
          <GradientButton
            onClick={() => navigate('/dashboard')}
            variant="outline"
            size="sm"
            icon={ArrowLeft}
          >
            Back to dashboard
          </GradientButton>
        }
      >
        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700">
            {successMessage}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          <GlassCard hover={false}>
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`panel-nav-item ${
                    activeTab === tab.id ? 'panel-nav-item-active' : ''
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </GlassCard>

          <div className="lg:col-span-3">
            <GlassCard hover={false}>
              {activeTab === 'profile' && renderProfileTab()}
              {activeTab === 'notifications' && renderNotificationsTab()}
            </GlassCard>
          </div>
        </div>
      </PanelShell>

      <ImageCropper
        isOpen={showCropper}
        onClose={() => setShowCropper(false)}
        onCropComplete={handleCropComplete}
        uploadedImage={profilePicture}
      />
    </>
  )
}

export default DashboardSettings 
